import React, { useState, useEffect } from 'react';
import { Card, Descriptions, Button, Spin, message, Space, Tabs, Table, Row, Col, Link } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStyles } from '../theme/hooks';
import { getCustomerById } from '../services/customerService';
import { getCustomerLoans } from '../services/loanService';
import { UserOutlined, MailOutlined, HomeOutlined, EditOutlined, ZoomInOutlined } from '@ant-design/icons';
import Page from '../components/layout/Page';
import CustomerForm from '../components/CustomerModal/CustomerForm';

const useCustomerDetailStyles = () => useStyles((theme) => ({
  container: {
    maxWidth: '2000px',
    margin: '0 auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.lg
  },
  card: {
    boxShadow: theme.shadows.sm,
    borderRadius: theme.borderRadius.lg,
    marginBottom: theme.spacing.lg
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px'
  }
}));

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [customer, setCustomer] = useState(null);
  const [loans, setLoans] = useState([]);  
  const [loading, setLoading] = useState(true);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const styles = useCustomerDetailStyles();
  const { Link } = require('react-router-dom');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customerData, loansData] = await Promise.all([
          getCustomerById(id),
          getCustomerLoans(id)
        ]);
        setCustomer(customerData);
        setLoans(Array.isArray(loansData) ? loansData : []); 
      } catch (error) {
        console.error('Error fetching customer data:', error);
        message.error(t('customer.fields.fetchError'));
        navigate('/customers');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate, t]);

  if (loading) {
    return (
      <Page
      title='Client Detail'>
        <div style={styles.loading}>
          <Spin size="large" />
        </div>
      </Page>
    );
  }

  if (!customer) {
    return null;
  }

  const loanColumns = [
    {
        title: t('common.actions'),
        key: 'actions',
        fixed: 'left',
        width: 150,
        render: (_, record) => (
            <Space size="middle">
                <Button 
                    onClick={() => navigate(`/loans/${record.id}`)}
                    icon={<ZoomInOutlined />}
                    title="View Details"
                >
                </Button>                    
                <Button 
                    onClick={() => navigate(`/loans/${record.id}/edit`)}
                    icon={<EditOutlined />}
                    title="Edit Loan"
                >
                </Button>
            </Space>
        ),
    },    
    {
      title: t('loan.fields.primaryConsultant'),
      key: 'primary_consultant',
      render: (_, record) => `${record.primary_consultant?.first_name} ${record.primary_consultant?.last_name}`
    },      
    {
      title: t('loan.fields.temporaryConsultant'),
      key: 'secondary_consultant',
      render: (_, record) => record.secondary_consultant ? 
          `${record.secondary_consultant.first_name} ${record.secondary_consultant.last_name}` : ''
    },
    {
      title: t('loan.fields.processNumber'),
      dataIndex: 'process_number',
      key: 'process_number',
      fixed: 'left',
      width: 150,
      render: (text, record) => <Link to={`/loans/${record.id}`}>{text}</Link>
    },
    {
      title: t('loan.fields.bank'),
      dataIndex: ['bank', 'name'],
      key: 'bank_name',
      fixed: 'left',
      width: 150,
      sorter: (a, b) => (a.bank?.name || '').localeCompare(b.bank?.name || ''),
  },
  {
      title: t('loan.fields.pooler'),
      dataIndex: ['pooler', 'name'],
      key: 'pooler_name',
      fixed: 'left',
      width: 150,
      sorter: (a, b) => (a.pooler?.name || '').localeCompare(b.pooler?.name || ''),
  },
    {
      title: t('loan.fields.amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => amount ? `${Number(amount).toLocaleString('de-DE')} €` : '-',
      align: 'right'
    },
    {
      title: t('loan.fields.evaluationAmount'),
      dataIndex: 'evaluation_amount',
      key: 'evaluation_amount',
      render: (evaluation_amount) => evaluation_amount ? `${Number(evaluation_amount).toLocaleString('de-DE')} €` : '-',
      align: 'right'
    },
    {
      title: t('Provision'),
      dataIndex: 'provision',
      key: 'provision',
      render: (provision) => provision ? `${Number(provision).toLocaleString('de-DE', { minimumFractionDigits: 4 })} %` : '-',
      align: 'right'
    },
    {
      title: t('loan.fields.extraBonus'),
      key: 'extra_bonus',
      render: (_, record) => record.commission_info?.primary_share ? 
          `${Number(record.commission_info.extra_bonus).toLocaleString('de-DE')} €` : '-',
      align: 'right'
    },
    {
      title: t('loan.fields.primaryShare'),
      key: 'commission',
      render: (_, record) => record.commission_info?.primary_share ? 
          `${Number(record.commission_info.primary_share).toLocaleString('de-DE')} €` : '-',
      align: 'right'
    },
    {
      title: t('loan.fields.detailed_status'),
      dataIndex: 'detailed_status',
      key: 'detailed_status',
      render: (text) => {
          const status = text.replace('LoanStatus.', '');
          return t(`loan.status.consultant.${status}`);
      }
  },
  ];

  const handleEditSuccess = (updatedCustomer) => {
    setCustomer(updatedCustomer);
    setIsEditModalVisible(false);
  };

  const headerExtra = (
    <Button type="primary" onClick={() => navigate(`/loans/create?customer=${customer.id}`)}>
      {t('loan.create')}
    </Button>
  );

  return (
    <Page 
      title={`${customer.name} ${customer.first_name || '-'}`}
      extra={headerExtra}
    >
      <div style={styles.container}>
        <div style={styles.header}>
          <h1>{customer.name}, {customer.first_name}</h1>
          <Button 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => setIsEditModalVisible(true)}
          >
            {t('common.edit')}
          </Button>
        </div>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Card 
              style={styles.card}
              title={<><UserOutlined /> {t('customer.fields.personalInfo')}</>}
            >
              <Descriptions bordered column={1}>
                <Descriptions.Item label={t('customer.fields.name')}>
                  {customer.name}
                </Descriptions.Item>
                <Descriptions.Item label={t('customer.fields.firstName')}>
                  {customer.first_name || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('customer.fields.dateOfBirth')}>
                  {customer.date_of_birth || '-'}
                </Descriptions.Item>                
              </Descriptions>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card 
              style={styles.card}
              title={<><MailOutlined /> {t('customer.fields.contactInfo')}</>}
            >
              <Descriptions bordered column={1}>
                <Descriptions.Item label={t('customer.fields.email')}>
                  {customer.email || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('customer.fields.phone')}>
                  {customer.phone || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('customer.fields.mobile')}>
                  {customer.mobile || '-'}
                </Descriptions.Item>                
              </Descriptions>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card 
              style={styles.card}
              title={<><HomeOutlined /> {t('customer.fields.address')}</>}
            >
              <Descriptions bordered column={1}>
                <Descriptions.Item label={t('customer.fields.street')}>
                  {customer.street + ' ' + customer.house_number || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('customer.fields.zipCode')}>
                  {customer.zip_code || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('customer.fields.city')}>
                  {customer.city || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
        <div style={{ marginTop: 24 }}>
            <h2>{t('customer.loans')}</h2>
            {Array.isArray(loans) ? (
              <Table
                columns={loanColumns}
                dataSource={loans}
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true
                }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                {t('common.no_data_available', 'No loan data available')}
              </div>
            )}
        </div>

        <CustomerForm
          visible={isEditModalVisible}
          onCancel={() => setIsEditModalVisible(false)}
          onSuccess={handleEditSuccess}
          initialData={customer}
        />
      </div>
    </Page>
  );
};

export default CustomerDetail;
